import type { ReactNode } from 'react'
import { AlertTriangle, Boxes, PackageX, TrendingUp } from 'lucide-react'
import { Card, CardContent } from '../ui/card'
import { formatCurrency, formatIsoDate, formatNumber } from '../../utils/dateFormat'
import type { PharmaKpis } from '../../types/pharma-analytics.types'

interface KpiCardsProps {
  data: PharmaKpis
  /** Учитывать ли хозяйственные счета (дезсредства) в стоимости запаса. */
  includeNonClinical: boolean
  onToggleNonClinical: (value: boolean) => void
}

interface TileProps {
  title: string
  value: string
  hint: ReactNode
  icon: ReactNode
  accent: string
  tone?: 'default' | 'warn' | 'alert'
}

function Tile({ title, value, hint, icon, accent, tone = 'default' }: TileProps) {
  const valueClass =
    tone === 'alert'
      ? 'text-destructive'
      : tone === 'warn'
        ? 'text-amber-600 dark:text-amber-500'
        : 'text-foreground'

  return (
    <Card className="hover-lift">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className={`text-2xl font-bold mt-1 tabular-nums ${valueClass}`}>
              {value}
            </p>
            <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
              {hint}
            </div>
          </div>
          <div
            className={`h-11 w-11 rounded-full flex items-center justify-center flex-shrink-0 ${accent}`}
          >
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export function KpiCards({
  data,
  includeNonClinical,
  onToggleNonClinical,
}: KpiCardsProps) {
  const stock = includeNonClinical ? data.stock.total : data.stock.clinical
  const { atRisk } = data.expiryRisk
  const { deadStock, movements } = data

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Tile
          title="Стоимость остатков"
          value={formatCurrency(stock.value, { compact: true })}
          hint={
            <>
              {formatNumber(stock.positions)} позиций ·{' '}
              {formatNumber(data.stock.medications)} наименований
              {stock.positionsWithoutPrice > 0 && (
                <>
                  <br />
                  цена известна у {stock.priceCoverage ?? 0}% —{' '}
                  {formatNumber(stock.positionsWithoutPrice)} позиций без цены
                </>
              )}
            </>
          }
          icon={<Boxes className="h-5 w-5 text-tco-blue-500" />}
          accent="bg-tco-blue-50"
        />

        <Tile
          title="Под риском списания"
          value={formatCurrency(atRisk.value, { compact: true })}
          hint={
            <>
              {formatNumber(atRisk.positions)} позиций со сроком до 90 дней
              <br />
              заглушки и нераспознанные сроки не в счёт
            </>
          }
          icon={<AlertTriangle className="h-5 w-5 text-destructive" />}
          accent="bg-destructive/10"
          tone={atRisk.positions > 0 ? 'alert' : 'default'}
        />

        <Tile
          title={`Без движения ${deadStock.windowDays}+ дней`}
          value={formatCurrency(deadStock.value, { compact: true })}
          hint={
            <>
              {formatNumber(deadStock.positions)} позиций
              {deadStock.shareOfStock !== null && (
                <> · {deadStock.shareOfStock}% стоимости запаса</>
              )}
              {!deadStock.historyCovered && (
                <>
                  <br />
                  истории движений хватает только с{' '}
                  {formatIsoDate(deadStock.movementsFrom)}
                </>
              )}
            </>
          }
          icon={<PackageX className="h-5 w-5 text-amber-600" />}
          accent="bg-amber-50 dark:bg-amber-950/40"
          tone={deadStock.positions > 0 ? 'warn' : 'default'}
        />

        <Tile
          title="Закупки за период"
          value={formatCurrency(movements.externalIn.cost, { compact: true })}
          hint={
            <>
              списано {formatCurrency(movements.externalOut.cost, { compact: true })}
              <br />
              перемещения между складами не в счёт
            </>
          }
          icon={<TrendingUp className="h-5 w-5 text-success-600" />}
          accent="bg-success-50"
        />
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="accent-tco-blue-500"
            checked={includeNonClinical}
            onChange={(e) => onToggleNonClinical(e.target.checked)}
          />
          Включая хозяйственные счета (
          {data.stock.nonClinicalAccounts.join(', ') || '—'}):{' '}
          {formatCurrency(data.stock.nonClinical.value, { compact: true })}
        </label>
        <span>Остатки на {formatIsoDate(data.asOf)}</span>
        <span>
          Движения по {formatIsoDate(data.dataThrough)} · период{' '}
          {formatIsoDate(data.period.from)} — {formatIsoDate(data.period.to)}
        </span>
      </div>
    </div>
  )
}
